import React, { useState, useEffect } from 'react';
import Modal from '@/components/shared/Modal';
import './PaymentResultModal.scss';
import { safeToLowerCase } from '@/utils/stringUtils';

interface PaymentDetails {
  id: string;
  amount: number | string; // Може бути і числом, і рядком
  currency: string;
  status: string;
  paymentMethod?: string;
  transactionId?: string;
  processedAt?: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
}

interface PaymentResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: 'success' | 'failure' | 'cancelled' | 'error';
  paymentId?: string;
  amount?: number;
  message?: string;
  onViewDetails?: (paymentId: string) => Promise<PaymentDetails>;
}

// Моковые данные для демонстрации
const mockPaymentDetails: PaymentDetails = {
  id: 'pay_1234567890',
  amount: '45.67',
  currency: 'USD',
  status: 'completed',
  paymentMethod: 'Visa ****3467',
  transactionId: 'txn_abcd1234',
  processedAt: new Date().toISOString(),
  description: 'Payment of 45.67 - Testing',
  customerName: 'Frank Mitchell',
  customerEmail: 'frank.mitchell@example.com'
};

const PaymentResultModal: React.FC<PaymentResultModalProps> = ({
                                                                 isOpen,
                                                                 onClose,
                                                                 status,
                                                                 paymentId,
                                                                 amount,
                                                                 message,
                                                                 onViewDetails
                                                               }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Скидаємо стан при закритті модалки
  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setPaymentDetails(null);
      setIsLoadingDetails(false);
    }
  }, [isOpen]);

  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: '✓',
          iconClass: 'PaymentResultModal__icon--success',
          title: 'Payment Successful!',
          titleClass: 'PaymentResultModal__title--success',
          description: message || 'Your payment has been processed successfully.',
          showDetails: true
        };
      case 'failure':
        return {
          icon: '✕',
          iconClass: 'PaymentResultModal__icon--error',
          title: 'Payment Failed',
          titleClass: 'PaymentResultModal__title--error',
          description: message || 'Your payment could not be processed. Please try again.',
          showDetails: false
        };
      case 'cancelled':
        return {
          icon: '⚠',
          iconClass: 'PaymentResultModal__icon--warning',
          title: 'Payment Cancelled',
          titleClass: 'PaymentResultModal__title--warning',
          description: message || 'You have cancelled the payment process.',
          showDetails: false
        };
      case 'error':
        return {
          icon: '✕',
          iconClass: 'PaymentResultModal__icon--error',
          title: 'Payment Error',
          titleClass: 'PaymentResultModal__title--error',
          description: message || 'An error occurred during payment processing.',
          showDetails: false
        };
      default:
        return {
          icon: 'ℹ',
          iconClass: 'PaymentResultModal__icon--info',
          title: 'Payment Status',
          titleClass: 'PaymentResultModal__title--info',
          description: message || 'Payment status is unknown.',
          showDetails: false
        };
    }
  };

  const handleToggleDetails = async () => {
    if (!isExpanded && !paymentDetails && paymentId && onViewDetails) {
      setIsLoadingDetails(true);
      try {
        const details = await onViewDetails(paymentId);
        setPaymentDetails(details);
      } catch (error) {
        console.error('Failed to load payment details:', error);
        // Якщо API не реалізоване, показуємо повідомлення
        console.warn('Payment details API not implemented, using mock data');
        setPaymentDetails({
          ...mockPaymentDetails,
          id: paymentId,
          amount: amount || mockPaymentDetails.amount
        });
      } finally {
        setIsLoadingDetails(false);
      }
    }
    setIsExpanded(!isExpanded);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(dateString));
  };

  const statusConfig = getStatusConfig();

  // SVG іконки
  const ChevronDownIcon: React.FC<{ isRotated: boolean }> = ({ isRotated }) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={isRotated ? 'PaymentResultModal__chevron--rotated' : ''}
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const LoadingSpinner: React.FC = () => (
    <div className="PaymentResultModal__spinner">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
          <animateTransform attributeName="transform" type="rotate" values="0 10 10;360 10 10" dur="1s" repeatCount="indefinite"/>
        </circle>
      </svg>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      showCloseButton={true}
      closeOnOverlayClick={true}
      closeOnEscape={true}
    >
      <div className="PaymentResultModal">
        {/* Status Header */}
        <div className="PaymentResultModal__header">
          <div className={`PaymentResultModal__icon ${statusConfig.iconClass}`}>
            {statusConfig.icon}
          </div>
          <h2 className={`PaymentResultModal__title ${statusConfig.titleClass}`}>
            {statusConfig.title}
          </h2>
          <p className="PaymentResultModal__description">
            {statusConfig.description}
          </p>
          {amount && (
            <div className="PaymentResultModal__amount">
              ${amount.toFixed(2)}
            </div>
          )}
        </div>

        {/* Payment Details Toggle */}
        {statusConfig.showDetails && paymentId && (
          <div className="PaymentResultModal__details-section">
            <button
              className="PaymentResultModal__details-toggle"
              onClick={handleToggleDetails}
              disabled={isLoadingDetails}
            >
              <span>Payment Details</span>
              {isLoadingDetails ? (
                <LoadingSpinner />
              ) : (
                <ChevronDownIcon isRotated={isExpanded} />
              )}
            </button>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="PaymentResultModal__details-content">
                {paymentDetails ? (
                  <div className="PaymentResultModal__details-grid">
                    <div className="PaymentResultModal__detail-row">
                      <span className="PaymentResultModal__detail-label">Payment ID:</span>
                      <span className="PaymentResultModal__detail-value PaymentResultModal__detail-value--mono">
                        {paymentDetails.id}
                      </span>
                    </div>

                    <div className="PaymentResultModal__detail-row">
                      <span className="PaymentResultModal__detail-label">Transaction ID:</span>
                      <span className="PaymentResultModal__detail-value PaymentResultModal__detail-value--mono">
                        {paymentDetails.transactionId || 'N/A'}
                      </span>
                    </div>

                    <div className="PaymentResultModal__detail-row">
                      <span className="PaymentResultModal__detail-label">Amount:</span>
                      <span className="PaymentResultModal__detail-value PaymentResultModal__detail-value--amount">
                        ${typeof paymentDetails.amount === 'number'
                        ? paymentDetails.amount.toFixed(2)
                        : parseFloat(paymentDetails.amount).toFixed(2)} {paymentDetails.currency}
                      </span>
                    </div>

                    <div className="PaymentResultModal__detail-row">
                      <span className="PaymentResultModal__detail-label">Payment Method:</span>
                      <span className="PaymentResultModal__detail-value">
                        {paymentDetails.paymentMethod || 'N/A'}
                      </span>
                    </div>

                    <div className="PaymentResultModal__detail-row">
                      <span className="PaymentResultModal__detail-label">Status:</span>
                      <span className={`PaymentResultModal__detail-value PaymentResultModal__status PaymentResultModal__status--${safeToLowerCase(paymentDetails.status)}`}>
                        {paymentDetails.status}
                      </span>
                    </div>

                    {paymentDetails.processedAt && (
                      <div className="PaymentResultModal__detail-row">
                        <span className="PaymentResultModal__detail-label">Processed At:</span>
                        <span className="PaymentResultModal__detail-value">
                          {formatDate(paymentDetails.processedAt)}
                        </span>
                      </div>
                    )}

                    {paymentDetails.description && (
                      <div className="PaymentResultModal__detail-row">
                        <span className="PaymentResultModal__detail-label">Description:</span>
                        <span className="PaymentResultModal__detail-value">
                          {paymentDetails.description}
                        </span>
                      </div>
                    )}

                    {paymentDetails.customerName && (
                      <div className="PaymentResultModal__detail-row">
                        <span className="PaymentResultModal__detail-label">Customer:</span>
                        <span className="PaymentResultModal__detail-value">
                          {paymentDetails.customerName}
                        </span>
                      </div>
                    )}

                    {paymentDetails.customerEmail && (
                      <div className="PaymentResultModal__detail-row">
                        <span className="PaymentResultModal__detail-label">Email:</span>
                        <span className="PaymentResultModal__detail-value">
                          {paymentDetails.customerEmail}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="PaymentResultModal__no-details">
                    No additional details available
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="PaymentResultModal__actions">
          <button
            className="PaymentResultModal__button PaymentResultModal__button--primary"
            onClick={onClose}
          >
            Continue
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentResultModal;